<template>
  <div class="exp-item">
    <div class="exp-row exp-row-title">
      <input ref="firstInputRef" v-model="local.title" type="text" class="exp-input" placeholder="Job title" />
      <input v-model="local.company" type="text" class="exp-input" placeholder="Company" />
    </div>
    <input v-model="local.location" type="text" class="exp-input exp-full" placeholder="Location (optional)" />
    <div class="exp-row exp-row-dates">
      <label class="exp-checkbox-row">
        <input v-model="local.isCurrent" type="checkbox" class="exp-checkbox" /> Current role
      </label>
      <div class="exp-date-group">
        <span class="exp-date-label">Start</span>
        <select v-model.number="local.startMonth" class="exp-input exp-short exp-select" aria-label="Start month">
          <option :value="null">Month</option>
          <option v-for="m in monthOptions" :key="m.value" :value="m.value">{{ m.label }}</option>
        </select>
        <input v-model.number="local.startYear" type="number" class="exp-input exp-short" placeholder="Year" aria-label="Start year" />
      </div>
      <div v-if="!local.isCurrent" class="exp-date-group">
        <span class="exp-date-label">End</span>
        <select v-model.number="local.endMonth" class="exp-input exp-short exp-select" aria-label="End month">
          <option :value="null">Month</option>
          <option v-for="m in monthOptions" :key="'end-' + m.value" :value="m.value">{{ m.label }}</option>
        </select>
        <input v-model.number="local.endYear" type="number" class="exp-input exp-short" placeholder="Year" aria-label="End year" />
      </div>
    </div>
    <textarea v-model="local.description" class="exp-textarea" rows="2" placeholder="Short description (optional)" />
    <div class="exp-bullets">
      <label class="exp-label">Bullets</label>
      <div v-for="(b, i) in local.bullets" :key="i" class="exp-bullet-row">
        <input v-model="local.bullets[i]" type="text" class="exp-input" placeholder="Bullet point" maxlength="500" />
        <button type="button" class="exp-bullet-remove" @click="removeBullet(i)" aria-label="Remove bullet"><i class="fa fa-times" aria-hidden="true"></i></button>
      </div>
      <button type="button" class="btn-add-bullet" @click="addBullet">+ Add bullet</button>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, defineExpose } from 'vue';

const MONTH_OPTIONS = [
  { value: 1, label: 'Jan' }, { value: 2, label: 'Feb' }, { value: 3, label: 'Mar' }, { value: 4, label: 'Apr' },
  { value: 5, label: 'May' }, { value: 6, label: 'Jun' }, { value: 7, label: 'Jul' }, { value: 8, label: 'Aug' },
  { value: 9, label: 'Sep' }, { value: 10, label: 'Oct' }, { value: 11, label: 'Nov' }, { value: 12, label: 'Dec' },
];
const monthOptions = MONTH_OPTIONS;

const props = defineProps({
  modelValue: {
    type: Object,
    default: () => ({
      title: '',
      company: '',
      location: '',
      startMonth: null,
      startYear: null,
      endMonth: null,
      endYear: null,
      isCurrent: false,
      description: '',
      bullets: ['']
    })
  }
});
const emit = defineEmits(['update:modelValue']);

function toMonthNumber(v) {
  if (v == null || v === '') return null;
  const n = typeof v === 'number' ? v : parseInt(String(v).trim(), 10);
  return (n >= 1 && n <= 12) ? n : null;
}

function clone(o) {
  return {
    ...o,
    startMonth: toMonthNumber(o.startMonth),
    endMonth: toMonthNumber(o.endMonth),
    bullets: Array.isArray(o.bullets) ? [...o.bullets] : ['']
  };
}

const local = ref(clone(props.modelValue));
const firstInputRef = ref(null);
let ignoreNextPropUpdate = false;

function focusFirstInput() {
  firstInputRef.value?.focus();
}

defineExpose({ focusFirstInput });

watch(
  () => props.modelValue,
  (v) => {
    if (ignoreNextPropUpdate) {
      ignoreNextPropUpdate = false;
      return;
    }
    local.value = clone(v);
  },
  { deep: true }
);

watch(
  local,
  () => {
    ignoreNextPropUpdate = true;
    emit('update:modelValue', clone(local.value));
  },
  { deep: true }
);

function addBullet() {
  local.value.bullets.push('');
}

function removeBullet(i) {
  local.value.bullets.splice(i, 1);
  if (local.value.bullets.length === 0) local.value.bullets.push('');
}
</script>

<style scoped>
.exp-item {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.exp-row {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  align-items: center;
}
.exp-row-title {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}
.exp-row-title .exp-input {
  min-width: 0;
}
.exp-row-dates {
  flex-wrap: wrap;
  align-items: flex-start;
}
.exp-date-group {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}
.exp-date-label {
  font-size: 12px;
  font-weight: 600;
  color: var(--text-muted);
  min-width: 36px;
}
.exp-input,
.exp-textarea {
  padding: 10px 14px;
  border: 1px solid var(--border);
  border-radius: 8px;
  font-size: 14px;
  font-family: inherit;
  background: #ffffff;
  color: var(--text-primary);
  box-sizing: border-box;
  transition: border-color 0.2s, box-shadow 0.2s;
}
.exp-input::placeholder,
.exp-textarea::placeholder {
  color: var(--text-muted);
  opacity: 1;
}
.exp-input:hover:not(:disabled),
.exp-textarea:hover:not(:disabled) {
  border-color: var(--input-border-hover, var(--border));
}
.exp-input:focus,
.exp-textarea:focus {
  outline: none;
  border-color: var(--focus-ring);
  box-shadow: 0 0 0 2px var(--focus-ring);
}
.exp-input:disabled,
.exp-textarea:disabled {
  background: var(--input-disabled-bg, var(--surface-2));
  opacity: 0.8;
  cursor: not-allowed;
}
.exp-full {
  width: 100%;
}
.exp-short {
  width: 100px;
  min-width: 80px;
}
.exp-select {
  cursor: pointer;
  appearance: auto;
}
.exp-textarea {
  width: 100%;
  resize: vertical;
  min-height: 60px;
}
.exp-checkbox-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  font-size: 14px;
  color: var(--text-primary, var(--app-text));
  cursor: pointer;
}
.exp-checkbox {
  width: auto;
  margin: 0;
}
.exp-bullets {
  margin-top: 8px;
}
.exp-label {
  display: block;
  font-weight: 600;
  margin-bottom: 8px;
  font-size: 14px;
  color: var(--text-primary, var(--app-text));
}
.exp-bullet-row {
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
  align-items: center;
}
.exp-bullet-row .exp-input {
  flex: 1;
  min-width: 0;
}
.exp-bullet-remove {
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--surface-2);
  color: var(--text-secondary);
  cursor: pointer;
  font-size: 12px;
  line-height: 1;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s, border-color 0.2s, background 0.2s;
}
.exp-bullet-remove:hover {
  color: var(--danger);
  border-color: var(--danger);
  background: var(--danger-subtle);
}
.btn-add-bullet {
  padding: 8px 16px;
  font-size: 14px;
  border-radius: 8px;
  border: 1px solid var(--border);
  background: var(--surface-2);
  color: var(--text-primary);
  cursor: pointer;
  margin-top: 4px;
  transition: background 0.2s;
}
.btn-add-bullet:hover {
  background: var(--surface-3);
}

@media (max-width: 768px) {
  .exp-item {
    gap: 12px;
  }
  .exp-row-title {
    grid-template-columns: 1fr;
    gap: 10px;
  }
  .exp-row-dates {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }
  .exp-checkbox-row {
    width: 100%;
  }
  .exp-date-group {
    display: grid;
    grid-template-columns: 44px 1fr 1fr;
    gap: 8px;
    width: 100%;
  }
  .exp-date-group .exp-short {
    width: 100%;
    min-width: 0;
    max-width: none;
  }
  .exp-input,
  .exp-textarea {
    font-size: 16px; /* avoid iOS zoom on focus */
  }
}
</style>
